import styled from 'styled-components';

import CardGroupStyled from '../../components/CardGroup';
import HeroStyled from '../../components/Hero';
import PaginationStyled from '../../components/Pagination';
import commonPageContentStyles from '../../styles/mixins/_commonPageContentStyles';

const dataCardGroups = [
  {
    id: 1,
    title: '1',
    content: '1',
    linkUrl: '1',
    cards: [
      {
        title: 'Colors',
        content:
          'The DHCS color palette is built on four brand colors from the logo - Blue (#17315A), Teal (#2D6E8D), Yellow (#F9A71C), and Orange (#E47225) - plus neutrals for text and backgrounds. The palette is organized into semantic token groups (Neutral, Primary, Action, Warning, Accent, Success, Error) with light and dark mode support and WCAG AA contrast compliance.', // tokens-allow: hex codes quoted in the card copy, as the frame writes them
        linkUrl: '/branding-styleguide/colors',
        image: '/images/branding-styleguide/branding-styleguide-colors@2x.webp',
      },
      {
        title: 'Typography',
        content:
          'DHCS uses Public Sans as the primary web font, with Segoe UI for documents and Noto Sans for translations. The type scale runs from Display (56px) through H1-H5 headings, Body, and Paragraph styles. Headings use DHCS Black (neutral dark). Body text is black. Minimum font size is 12pt (16px on web, 18pt in PowerPoint).',
        linkUrl: '/branding-styleguide/typography',
        image: '/images/branding-styleguide/branding-styleguide-typography@2x.webp',
      },
      {
        title: 'Logos',
        content:
          'The DHCS logo system includes the Department logo (official DHCS mark for stakeholders and providers) and Program logos (Poppy graphic + Program Name for public-facing communications like Medi-Cal). Each logo has Full-Color, White + Color, White, and Black variants with clear space rules, minimum sizes, and strict misuse restrictions.',
        linkUrl: '/branding-styleguide/logos',
        image: '/images/branding-styleguide/branding-styleguide-logos@2x.webp',
      },
    ],
  },
  {
    id: 2,
    title: '1',
    content: '1',
    linkUrl: '1',
    cards: [
      {
        title: 'Iconography & Graphics',
        content:
          'The icon library uses Font Awesome Pro (Solid) for a consistent, filled icon style across the system. Icons are built as 16x16 component frames with 14px vectors centered inside. The set covers UI actions, navigation, data visualization (charts, maps, databases, clouds), layout, typography tools, and filetypes - with token-bound colors that adapt to component state.',
        linkUrl: '/branding-styleguide/icons',
        image: '/images/branding-styleguide/branding-styleguide-icons@2x.webp',
      },
      {
        title: 'Imagery',
        content:
          'Photography should be people-focused, relatable, and reflect the diversity of the communities DHCS serves. Styles vary between candid, staged, and abstract based on the message. The iconography style for the Department brand is one-color and bold (solid fill), while Program brand icons use line style highlighted by a colored bar for a clean, approachable look.',
        linkUrl: '/branding-styleguide/imagery',
        image: '/images/branding-styleguide/branding-styleguide-imagery@2x.webp',
      },
      {
        title: 'Interactions and Animations',
        content:
          'Interaction patterns define how components respond to user input - hover, focus, active, and disabled states. The button system includes Primary (Teal), Secondary (outline), Standout (Yellow), Ghost, Default, Subtle, and Destructive types, each with a Focus state (2px ring in #4F859F meeting WCAG 3:1). Animations follow purposeful, subtle principles.', // tokens-allow: hex codes quoted in the card copy, as the frame writes them
        linkUrl: '/branding-styleguide/interactions-and-animations',
        image: '/images/branding-styleguide/branding-styleguide-interactions@2x.webp',
      },
    ],
  },
  {
    id: 3,
    title: '1',
    content: '1',
    linkUrl: '1',
    cards: [
      {
        title: 'Brand in Use',
        content:
          'Brand in Use shows how the DHCS identity is applied in real-world materials — flyer anatomy, co-branding with external organizations, and correct header, footer and logo placement — with the rules that keep every application consistent and on-brand.',
        linkUrl: '/branding-styleguide/brand-in-use',
        image: '/images/branding-styleguide/branding-styleguide-brand-in-use@2x.webp',
      },
    ],
  },
];

type BrandingStyleguideStructureProps = {
  className?: string;
};

const BrandingStyleguideStructure = ({ className = '' }: BrandingStyleguideStructureProps) => (
  <div className={className}>
    <div className="content">
      <HeroStyled
        title="Branding Styleguide"
        subtitle={
          <p>
            The DHCS Branding Styleguide is the foundation of our visual identity. It defines how
            the Department of Health Care Services presents itself across all communications -
            printed and digital - to ensure consistency, accessibility, and professionalism. Use
            these guidelines to create on-brand materials that reflect our mission of providing
            equitable access to quality health care for all Californians.
          </p>
        }
        category
      />
      <div>
        {dataCardGroups.map((cardGroup) => (
          <CardGroupStyled
            key={cardGroup.id}
            title={cardGroup.title}
            content={cardGroup.content}
            linkUrl={cardGroup.linkUrl}
            cards={cardGroup.cards}
            category
          />
        ))}
      </div>
      <PaginationStyled
        previousLink="/"
        previous="Get Started"
        previousDisabled="disabled"
        nextLink="/general-components"
        next="General Components"
      />
    </div>
  </div>
);

const BrandingStyleguide = styled(BrandingStyleguideStructure)`
  ${commonPageContentStyles}
  height: 100%;
  overflow: unset;

  .content {
    padding: 0;
  }

  /* The Updated main frame (13727:145480) draws a 335px banner. The shared Hero keeps its own height
     for the pages with no Updated frame. */
  .hero-category {
    min-height: 20.9375rem;
  }
`;

export default BrandingStyleguide;
